import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

import {
  MlaApiResponse,
  MlaAssetRequest,
  MlaBorrowerCommitRequest,
  MlaCreditAuthorizationRequest,
  MlaDeclarationsRequest,
  MlaHmdaRequest,
  MlaIncomeRequest,
  MlaPropertyUpdateRequest,
  MlaRegistrationRequest,
} from '@ld.consumer-portal/models';
import { MlaAuthService } from './mla-auth.service';
import { MlaBorrowerService } from './mla-borrower.service';
import { MlaLoanService } from './mla-loan.service';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class MlaPurchaseService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(MlaAuthService);
  private readonly borrowerService = inject(MlaBorrowerService);
  private readonly loanService = inject(MlaLoanService);
  private readonly loggingService = inject(LoggingService);

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  async saveRegistration(
    loanId: string | null,
    borrowerId: string | null,
    data: MlaRegistrationRequest
  ): Promise<MlaApiResponse<{ loanId: string; borrowerId: string }>> {
    const url = `${this.mlaApiUrl}api/v1/purchase/registration`;

    try {
      const response = await firstValueFrom(
        this.http.post<MlaApiResponse<{ loanId: string; borrowerId: string }>>(url, {
          loanId,
          borrowerId,
          ...data,
        })
      );

      if (response?.success && response.data) {
        return response;
      }

      return { success: false, error: 'Failed to save registration' };
    } catch (error) {
      this.loggingService.logException(error, 'MlaPurchaseService: Error saving registration');
      return { success: false, error: 'Failed to save registration' };
    }
  }

  async updateProperty(loanId: string, data: MlaPropertyUpdateRequest): Promise<MlaApiResponse> {
    const subjectProperty = {
      address: {
        street: data.street || '',
        unit: data.unit || '',
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
      },
      propertyType: data.propertyType,
      occupancyType: data.occupancyType,
      purchasePrice: data.purchasePrice,
      loanAmount: data.purchasePrice && data.downPayment ? data.purchasePrice - data.downPayment : undefined,
      targetClosingDate: data.targetClosingDate?.toISOString(),
    };

    const success = await this.loanService.updateLoan(loanId, { subjectProperty });
    return { success };
  }

  async commitPrimaryBorrower(borrowerId: string, data: MlaBorrowerCommitRequest): Promise<MlaApiResponse> {
    const url = `${this.mlaApiUrl}api/v1/borrower/${borrowerId}/commit`;

    try {
      const response = await firstValueFrom(
        this.http.post<MlaApiResponse>(url, {
          dateOfBirth: data.dateOfBirth?.toISOString(),
          maritalStatus: data.maritalStatus,
          relationshipStatus: data.relationshipStatus,
          includeCoBorrower: data.includeCoBorrower,
          includeSpouse: data.includeSpouse,
        })
      );

      return response || { success: false };
    } catch (error) {
      this.loggingService.logException(error, 'MlaPurchaseService: Error committing borrower');
      return { success: false, error: 'Failed to commit borrower' };
    }
  }

  async saveDeclarations(borrowerId: string, data: MlaDeclarationsRequest): Promise<MlaApiResponse> {
    const url = `${this.mlaApiUrl}api/v1/borrower/${borrowerId}/declarations`;

    try {
      const response = await firstValueFrom(this.http.put<MlaApiResponse>(url, data));

      return response || { success: false };
    } catch (error) {
      this.loggingService.logException(error, 'MlaPurchaseService: Error saving declarations');
      return { success: false, error: 'Failed to save declarations' };
    }
  }

  async saveHmda(borrowerId: string, data: MlaHmdaRequest): Promise<MlaApiResponse> {
    const url = `${this.mlaApiUrl}api/v1/borrower/${borrowerId}/hmda`;

    try {
      const response = await firstValueFrom(this.http.put<MlaApiResponse>(url, data));

      return response || { success: false };
    } catch (error) {
      this.loggingService.logException(error, 'MlaPurchaseService: Error saving HMDA');
      return { success: false, error: 'Failed to save HMDA' };
    }
  }

  async saveIncome(borrowerId: string, data: MlaIncomeRequest): Promise<MlaApiResponse<{ incomeId: string }>> {
    const url = `${this.mlaApiUrl}api/v1/borrower/${borrowerId}/income`;

    try {
      const response = await firstValueFrom(this.http.post<MlaApiResponse<{ incomeId: string }>>(url, data));

      return response || { success: false };
    } catch (error) {
      this.loggingService.logException(error, 'MlaPurchaseService: Error saving income');
      return { success: false, error: 'Failed to save income' };
    }
  }

  async saveAsset(borrowerId: string, data: MlaAssetRequest): Promise<MlaApiResponse<{ assetId: string }>> {
    const url = `${this.mlaApiUrl}api/v1/borrower/${borrowerId}/asset`;

    try {
      const response = await firstValueFrom(this.http.post<MlaApiResponse<{ assetId: string }>>(url, data));

      return response || { success: false };
    } catch (error) {
      this.loggingService.logException(error, 'MlaPurchaseService: Error saving asset');
      return { success: false, error: 'Failed to save asset' };
    }
  }

  async submitCreditAuthorization(
    loanId: string,
    borrowerId: string,
    data: MlaCreditAuthorizationRequest
  ): Promise<MlaApiResponse> {
    const url = `${this.mlaApiUrl}api/v1/loan/${loanId}/credit-authorization`;

    try {
      const response = await firstValueFrom(
        this.http.post<MlaApiResponse>(url, {
          borrowerId,
          ssn: data.ssn,
          creditAuthorizationAccepted: data.creditAuthorizationAccepted,
        })
      );

      return response || { success: false };
    } catch (error) {
      this.loggingService.logException(error, 'MlaPurchaseService: Error submitting credit authorization');
      return { success: false, error: 'Failed to submit credit authorization' };
    }
  }

  async submitApplication(loanId: string): Promise<MlaApiResponse> {
    const url = `${this.mlaApiUrl}api/v1/loan/${loanId}/submit`;

    try {
      const response = await firstValueFrom(this.http.post<MlaApiResponse>(url, null));

      return response || { success: false };
    } catch (error) {
      this.loggingService.logException(error, 'MlaPurchaseService: Error submitting application');
      return { success: false, error: 'Failed to submit application' };
    }
  }

  async createCoborrower(
    loanId: string,
    data: {
      firstName: string;
      lastName: string;
      middleName?: string;
      suffix?: string;
      email: string;
      phone: string;
    }
  ): Promise<MlaApiResponse<{ borrowerId: string }>> {
    const url = `${this.mlaApiUrl}api/v1/loan/${loanId}/coborrower`;

    try {
      const response = await firstValueFrom(this.http.post<MlaApiResponse<{ borrowerId: string }>>(url, data));

      return response || { success: false };
    } catch (error) {
      this.loggingService.logException(error, 'MlaPurchaseService: Error creating coborrower');
      return { success: false, error: 'Failed to create coborrower' };
    }
  }
}
